/**
 * Group Documents (groupdocs) - Ops-side entity.
 *
 * Wraps the BFF's /api/groupdocs/* surface for the group Documents tab:
 * provisioning state + runs (Admin/Ops.Admin/Ops.SiteProvisioner) and the
 * delegated browse/upload endpoints (any session; SharePoint security
 * trimming applies because the BFF uses the USER's Graph token, so a 403
 * here means the user lacks SharePoint access, not a bug).
 * The Catalog package has its own groupdocs entity for the AUTHORING
 * surface (structure modules); this one covers sites.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bffFetch } from '@shared/api';
import { can } from '@shared/auth';
import { assertGuid } from '@shared/lib';

export type GroupSiteStatus = 'pending' | 'provisioning' | 'active' | 'error' | 'archived';

export interface GroupSite {
  id: string;
  group_id: string;
  site_url: string | null;
  status: GroupSiteStatus;
  group_name: string | null;
  account_number: string | null;
  eff_date: string | null;
  provisioned_at: string | null;
  provisioned_by: string | null;
}

export interface ProvisionLogRow {
  id: string;
  step: string;
  outcome: 'ok' | 'skipped' | 'error';
  detail: string;
  created_at: string;
}

export interface GroupSiteInfo {
  site: GroupSite | null;
  module_ids: string[];
  log: ProvisionLogRow[];
}

export interface DocModuleSummary {
  id: string;
  slug: string;
  name: string;
  description: string;
  is_base: boolean;
  active: boolean;
  solution_ids: string[];
}

export interface BrowseItem {
  id: string;
  name: string;
  is_folder: boolean;
  child_count: number | null;
  // Split of child_count; null when the BFF could not expand the folder's
  // children (the UI then falls back to the combined count).
  folder_count: number | null;
  file_count: number | null;
  size: number | null;
  web_url: string | null;
  modified_at: string | null;
  modified_by: string | null;
}

export type DocLibrary = 'internal' | 'external';

export interface DocSearchResult {
  id: string;
  name: string;
  is_folder: boolean;
  size: number | null;
  web_url: string | null;
  modified_at: string | null;
  modified_by: string | null;
  library: DocLibrary;
  /** Folder path inside the library ('' = library root). */
  folder_path: string;
}

/** Hub site: search from there is scoped to ALL group sites. Mirrors the
 *  GROUPDOCS_HUB_SITE_URL default in bff config. */
export const HUB_SITE_URL = 'https://oneveracity.sharepoint.com/sites/vera-groups-hub';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await bffFetch(`/api/groupdocs${path}`, init);
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      if (body.error) message = body.message ?? body.error;
    } catch {
      /* keep status message */
    }
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const groupdocsKeys = {
  all: ['ops-groupdocs'] as const,
  site: (groupId: string) => [...groupdocsKeys.all, 'site', groupId] as const,
  modules: () => [...groupdocsKeys.all, 'modules'] as const,
  browse: (groupId: string, library: DocLibrary, path: string) =>
    [...groupdocsKeys.all, 'browse', groupId, library, path] as const,
  search: (groupId: string, q: string) => [...groupdocsKeys.all, 'search', groupId, q] as const,
};

export interface SiteStatusRow {
  group_id: string;
  status: GroupSiteStatus;
}

/**
 * Org-wide provisioning roster (group id + status only) — feeds Operation
 * Daylight's "site provisioned" harbor dimension. No SharePoint content.
 */
export function useSiteStatusRoster(enabled = true) {
  return useQuery({
    queryKey: [...groupdocsKeys.all, 'site-roster'] as const,
    queryFn: () => request<{ sites: SiteStatusRow[] }>('/sites'),
    enabled,
    staleTime: 60 * 1000,
    // Fail soft: an unreachable roster leaves harbors dark on that axis.
    retry: false,
  });
}

/** Site state + provisioning log; polls while a run is in flight. */
export function useGroupSite(groupId: string | undefined) {
  return useQuery({
    queryKey: groupdocsKeys.site(groupId ?? ''),
    queryFn: () => request<GroupSiteInfo>(`/sites/${groupId}`),
    enabled: Boolean(groupId),
    refetchInterval: (query) =>
      query.state.data?.site?.status === 'provisioning' ? 3000 : false,
  });
}

export function useDocModules() {
  return useQuery({
    queryKey: groupdocsKeys.modules(),
    queryFn: () => request<DocModuleSummary[]>('/modules'),
    staleTime: 5 * 60 * 1000,
  });
}

export interface ProvisionRequest {
  group_name: string;
  account_number: string;
  account_id?: string | null;
  account_executive?: string | null;
  eff_date?: string | null;
  /** Plan-year start dates; {EffYear} folders expand once per year. */
  plan_years?: string[];
  module_ids: string[];
}

export function useProvisionSite(groupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProvisionRequest) =>
      request<{ status: string }>(`/sites/${groupId}/provision`, json(body)),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: groupdocsKeys.site(groupId) }),
  });
}

// ---------------------------------------------------------------------------
// Provisioning troubleshooting (admin): stall detection, reset, diagnose,
// and the richer admin roster. A run is fire-and-forget on the BFF, so a dead
// worker leaves status='provisioning' forever; this surface breaks that lock
// and explains missing access. Design: the Documents tab + the Group Site
// Provisioning admin page.
// ---------------------------------------------------------------------------

/** Mirror of the BFF STALL_THRESHOLD_MS: a 'provisioning' run this quiet is
 *  treated as orphaned and offered a reset. Kept in sync by hand. */
export const PROVISION_STALL_MS = 10 * 60 * 1000;

export const adminSitesKey = [...groupdocsKeys.all, 'admin-sites'] as const;

/** One row of the admin provisioning roster (group_site + latest step). */
export interface AdminSiteRow {
  id: string;
  group_id: string;
  group_name: string | null;
  account_number: string | null;
  site_url: string | null;
  status: GroupSiteStatus;
  provisioned_at: string | null;
  provisioned_by: string | null;
  created_at: string;
  last_step: string | null;
  last_outcome: 'ok' | 'skipped' | 'error' | null;
  last_step_at: string | null;
  last_step_age_seconds: number | null;
  stalled: boolean;
}

export interface SiteDiagnosis {
  site: GroupSite | null;
  lastStep: { step: string; outcome: string; created_at: string } | null;
  lastStepAgeSeconds: number | null;
  stalled: boolean;
  stallThresholdSeconds: number;
  stepOutcomes: Record<string, 'ok' | 'skipped' | 'error'>;
  checks: {
    graphSiteResolves: boolean | null;
    opsStaffInMembers: boolean | null;
    callerInOpsStaff: boolean | null;
  };
  opsStaffGroupId: string;
  siteOwnerUpn: string;
}

/** Admin roster of every group site + provisioning state; polls while any run
 *  is in flight so a completing run clears its own stalled flag. */
export function useAdminSites(enabled = true) {
  return useQuery({
    queryKey: adminSitesKey,
    queryFn: () =>
      request<{ sites: AdminSiteRow[]; stall_threshold_seconds: number }>('/admin/sites'),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.sites?.some((s) => s.status === 'provisioning') ? 5000 : false,
  });
}

/** On-demand diagnosis (runs live tenant probes); a mutation so it fires on a
 *  button click rather than polling. */
export function useDiagnoseSite() {
  return useMutation({
    mutationFn: (vars: { groupId: string }) =>
      request<SiteDiagnosis>(`/sites/${vars.groupId}/diagnose`),
  });
}

/** Break a stuck run: flips a stalled 'provisioning' row to 'error' so the
 *  idempotent re-run can proceed. `force` overrides the "still active" guard. */
export function useResetProvisioning() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { groupId: string; force?: boolean }) =>
      request<{ status: string }>(
        `/sites/${vars.groupId}/reset${vars.force ? '?force=true' : ''}`,
        { method: 'POST' }
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: adminSitesKey });
      queryClient.invalidateQueries({ queryKey: groupdocsKeys.site(vars.groupId) });
    },
  });
}

/** Minimal re-run from the admin roster (base + previously-applied modules;
 *  the provisioner unions module ids, so an empty list keeps prior modules).
 *  Enough to finish an interrupted run and grant access. */
export function useReprovisionSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { groupId: string; group_name: string; account_number: string }) =>
      request<{ status: string }>(
        `/sites/${vars.groupId}/provision`,
        json({ group_name: vars.group_name, account_number: vars.account_number, module_ids: [] })
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: adminSitesKey });
      queryClient.invalidateQueries({ queryKey: groupdocsKeys.site(vars.groupId) });
    },
  });
}

export function useBrowse(
  groupId: string | undefined,
  library: DocLibrary,
  path: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: groupdocsKeys.browse(groupId ?? '', library, path),
    queryFn: () =>
      request<{ items: BrowseItem[] }>(
        `/sites/${groupId}/browse?library=${library}&path=${encodeURIComponent(path)}`
      ),
    enabled: Boolean(groupId) && enabled,
    staleTime: 30 * 1000,
    retry: (failureCount, error) => {
      const status = (error as Error & { status?: number }).status;
      // 403 = no SharePoint access, 409 = reauth needed; retrying won't help.
      return status !== 403 && status !== 409 && failureCount < 2;
    },
  });
}

export function useUploadFile(groupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      library,
      path,
      file,
    }: {
      library: DocLibrary;
      path: string;
      file: File;
    }) => {
      const form = new FormData();
      form.append('file', file);
      // No Content-Type header: the browser sets the multipart boundary.
      const res = await bffFetch(
        `/api/groupdocs/sites/${groupId}/upload?library=${library}&path=${encodeURIComponent(path)}`,
        { method: 'POST', body: form }
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
        throw new Error(body.message ?? body.error ?? `Upload failed (${res.status})`);
      }
      return (await res.json()) as { id: string; name: string };
    },
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({
        queryKey: groupdocsKeys.browse(groupId, vars.library, vars.path),
      }),
  });
}

/** Search both libraries of the group's site (delegated; security trimmed). */
export function useDocSearch(groupId: string | undefined, q: string) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: groupdocsKeys.search(groupId ?? '', trimmed),
    queryFn: () =>
      request<{ results: DocSearchResult[] }>(
        `/sites/${groupId}/search?q=${encodeURIComponent(trimmed)}`
      ),
    enabled: Boolean(groupId) && trimmed.length >= 2,
    staleTime: 30 * 1000,
    retry: (failureCount, error) => {
      const status = (error as Error & { status?: number }).status;
      return status !== 403 && status !== 409 && failureCount < 2;
    },
  });
}

export function canProvisionSites(roles: readonly string[]): boolean {
  return can('ops.provision-sites', roles);
}

/** Narrower than provisioning: who may decide that EVERY new group gets a site. */
export function canManageSiteAutomation(roles: readonly string[]): boolean {
  return can('ops.manage-site-automation', roles);
}

// ---------------------------------------------------------------------------
// Book-wide coverage + batch stamping (ADO #1488)
// ---------------------------------------------------------------------------

export type CoverageStatus =
  | 'active'
  | 'provisioning'
  | 'stalled'
  | 'error'
  | 'pending'
  | 'archived'
  | 'missing'
  | 'blocked';

/** One active Vera group and the state of its document site, site or not. */
export interface CoverageRow {
  groupId: string;
  accountId: string | null;
  groupName: string;
  accountNumber: string;
  accountExecutive: string | null;
  status: CoverageStatus;
  siteUrl: string | null;
  lastStep: string | null;
  lastOutcome: 'ok' | 'skipped' | 'error' | null;
  lastStepAt: string | null;
  derivedModuleIds: string[];
  derivedModuleNames: string[];
  appliedModuleIds: string[];
  missingModuleNames: string[];
  blockedReason: string | null;
  planYears: string[];
}

export interface CoverageSummary {
  total: number;
  active: number;
  missing: number;
  blocked: number;
  error: number;
  stalled: number;
  provisioning: number;
  other: number;
  needsModules: number;
}

export interface Coverage {
  rows: CoverageRow[];
  summary: CoverageSummary;
  modules: { id: string; name: string; is_base: boolean }[];
  max_batch: number;
  /** False = Catalog solution names were unreadable, so only the base module
   *  could be derived. Say so; base-only looks identical to "no solutions". */
  derivationAvailable: boolean;
  /** Active product modules with no Catalog solution mapped: they can never be
   *  derived, which reads as a bug until someone maps them in Catalog. */
  unmappedModuleNames: string[];
}

export interface BatchPlanItem {
  groupId: string;
  groupName: string;
  accountNumber: string;
  action: 'provision' | 'sync';
  moduleNames: string[];
  planYears: string[];
}

export interface BatchSkip {
  groupId: string;
  groupName: string;
  reason: string;
}

export interface BatchResult {
  queued: BatchPlanItem[];
  skipped: BatchSkip[];
  dry_run: boolean;
}

export const coverageKey = [...groupdocsKeys.all, 'coverage'] as const;
export const autoStampKey = [...groupdocsKeys.all, 'auto-stamp'] as const;

/**
 * Every active Vera group with its site state and derived modules. Heavier
 * than the sites roster (it reads the book from Dataverse), so it polls only
 * while a run is in flight and otherwise stays put until something invalidates
 * it.
 */
export function useSiteCoverage(enabled = true) {
  return useQuery({
    queryKey: coverageKey,
    queryFn: () => request<Coverage>('/admin/coverage'),
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: (query) =>
      query.state.data?.rows?.some((r) => r.status === 'provisioning') ? 10000 : false,
  });
}

/**
 * Stamp a batch. Call with dryRun to get the plan for the confirmation step,
 * then again without it to run. The BFF caps the batch and re-plans server
 * side, so the preview is advisory, never the authority.
 */
export function useBatchStamp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { groupIds: string[]; dryRun?: boolean }) =>
      request<BatchResult>(
        '/admin/batch-stamp',
        json({ group_ids: vars.groupIds, dry_run: vars.dryRun === true })
      ),
    onSuccess: (_data, vars) => {
      if (vars.dryRun) return;
      queryClient.invalidateQueries({ queryKey: coverageKey });
      queryClient.invalidateQueries({ queryKey: adminSitesKey });
    },
  });
}

export interface DerivedModules {
  module_ids: string[];
  module_names: string[];
  plan_years: string[];
  blocked_reason: string | null;
}

/**
 * The structure modules one group's enrolled solutions call for. Same resolver
 * a batch uses, so a hand-provisioned site and a batch-provisioned site get the
 * same folders. Fails soft: no answer just means nothing is pre-checked.
 */
export function useDerivedModules(groupId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...groupdocsKeys.all, 'derived-modules', groupId ?? ''] as const,
    queryFn: () =>
      request<DerivedModules>(`/groups/${assertGuid(groupId!, 'groupId')}/derived-modules`),
    enabled: Boolean(groupId) && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export interface AutoStampSetting {
  enabled: boolean;
  updatedAt: string | null;
  updatedBy: string;
}

export function useAutoStamp(enabled = true) {
  return useQuery({
    queryKey: autoStampKey,
    queryFn: () => request<AutoStampSetting>('/admin/auto-stamp'),
    enabled,
  });
}

export function useSetAutoStamp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { enabled: boolean }) =>
      request<AutoStampSetting>('/admin/auto-stamp', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: vars.enabled }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(autoStampKey, data);
    },
  });
}

/**
 * Fire the auto-stamp hook for a freshly created group. Best-effort by design:
 * the BFF decides whether auto-stamp is on and always answers 200, so callers
 * never branch on failure and enrollment never waits on SharePoint.
 */
export async function autoStampGroup(groupId: string): Promise<void> {
  try {
    await request<{ stamped: boolean; reason: string }>(
      `/auto-stamp/${assertGuid(groupId, 'groupId')}`,
      { method: 'POST' }
    );
  } catch {
    /* enrollment succeeded; the site can be provisioned from the Documents tab */
  }
}

// ---------------------------------------------------------------------------
// External guest roster (GUEST-ACCESS.md). Staging never emails; fire/send
// run through the BFF's mode-guarded mailer and are gated separately.
// ---------------------------------------------------------------------------

export type InviteMode = 'off' | 'sink' | 'live';
export type GuestAccessStatus = 'none' | 'granted' | 'revoked';

export interface RosterRow {
  id: string;
  contact_id: string;
  display_name: string;
  email: string | null;
  invite_flag: boolean;
  verified_at: string | null;
  verified_by: string | null;
  access_status: GuestAccessStatus;
  granted_at: string | null;
  revoked_at: string | null;
  redeemed_at: string | null;
  send_count: number;
  last_kind: string | null;
  last_status: 'sending' | 'sent' | 'failed' | null;
  last_mode: string | null;
  last_at: string | null;
  last_error: string | null;
}

export interface RosterInfo {
  mode: InviteMode;
  sink: string;
  rows: RosterRow[];
}

export interface CandidateContact {
  contact_id: string;
  full_name: string;
  email: string | null;
  job_title: string | null;
  source: 'group' | 'broker' | 'broker-agency';
}

/** Accounts a NEW contact may be created under from the roster picker: the
 *  group, plus the broker's agency when there is a broker of record. */
export interface CandidateParentAccount {
  account_id: string;
  name: string;
  kind: 'group' | 'broker-agency';
}

export interface PreflightRow {
  id: string;
  display_name: string;
  email: string | null;
  will_send: boolean;
  skip_reason: string | null;
}

export interface PreflightResult {
  mode: InviteMode;
  sink: string;
  rows: PreflightRow[];
  sendable_count: number;
}

export const rosterKeys = {
  roster: (groupId: string) => [...groupdocsKeys.all, 'roster', groupId] as const,
  candidates: (groupId: string) => [...groupdocsKeys.all, 'roster-candidates', groupId] as const,
};

/** Roster + mode; polls while any row's email is in flight (batch fire). */
export function useRoster(groupId: string | undefined) {
  return useQuery({
    queryKey: rosterKeys.roster(groupId ?? ''),
    queryFn: () => request<RosterInfo>(`/sites/${groupId}/roster`),
    enabled: Boolean(groupId),
    refetchInterval: (query) =>
      query.state.data?.rows.some((r) => r.last_status === 'sending') ? 3000 : false,
  });
}

export function useRosterCandidates(groupId: string, enabled: boolean) {
  return useQuery({
    queryKey: rosterKeys.candidates(groupId),
    queryFn: () =>
      request<{ candidates: CandidateContact[]; accounts: CandidateParentAccount[] }>(
        `/sites/${groupId}/roster/candidates`
      ),
    enabled,
    staleTime: 60 * 1000,
  });
}

function useRosterMutation<TVars>(groupId: string, fn: (vars: TVars) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => queryClient.invalidateQueries({ queryKey: rosterKeys.roster(groupId) }),
  });
}

export function useAddRosterContact(groupId: string) {
  return useRosterMutation(groupId, (vars: { contact_id: string }) =>
    request(`/sites/${groupId}/roster`, json(vars))
  );
}

export function usePatchRosterRow(groupId: string) {
  return useRosterMutation(groupId, (vars: { rowId: string; invite_flag?: boolean; verify?: boolean }) =>
    request(`/sites/${groupId}/roster/${vars.rowId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invite_flag: vars.invite_flag, verify: vars.verify }),
    })
  );
}

export function useRemoveRosterRow(groupId: string) {
  return useRosterMutation(groupId, (vars: { rowId: string }) =>
    request(`/sites/${groupId}/roster/${vars.rowId}`, { method: 'DELETE' })
  );
}

/** Dry-run classification; safe to call freely (never emails). */
export function usePreflight(groupId: string) {
  return useMutation({
    mutationFn: () => request<PreflightResult>(`/sites/${groupId}/roster/preflight`, json({})),
  });
}

export function useFireBatch(groupId: string) {
  return useRosterMutation(groupId, (vars: { row_ids: string[]; confirmed_count: number }) =>
    request<{ queued: number }>(`/sites/${groupId}/roster/fire`, json(vars))
  );
}

export function useSendInvite(groupId: string) {
  return useRosterMutation(groupId, (vars: { rowId: string }) =>
    request(`/sites/${groupId}/roster/${vars.rowId}/send`, json({}))
  );
}

export function useRevokeGuest(groupId: string) {
  return useRosterMutation(groupId, (vars: { rowId: string }) =>
    request(`/sites/${groupId}/roster/${vars.rowId}/revoke`, json({}))
  );
}

export function canStageGuestRoster(roles: readonly string[]): boolean {
  return can('ops.stage-guest-roster', roles);
}

export function canManageGuestInvites(roles: readonly string[]): boolean {
  return can('ops.manage-guest-invites', roles);
}
